// Update: components/ColorPicker.tsx
import { css } from 'aphrodite';
import React, { useEffect, useRef, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { styleSheet } from './styles';

interface ColorPickerProps {
	value: string;
	onChange: (color: string) => void;
}

function getContrastTextColor(hexBg: string): string {
	const shorthand = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hexBg);
	if (shorthand) {
		hexBg = '#' + shorthand[1] + shorthand[1] + shorthand[2] + shorthand[2] + shorthand[3] + shorthand[3];
	}
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexBg);
	if (!result) return '#ffffff';
	const channels = [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)].map(c => {
		c = c / 255;
		return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	});
	const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
	return luminance > 0.179 ? '#000000' : '#ffffff';
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange }) => {
	const [isOpen, setIsOpen] = useState(false);
	const popoverRef = useRef<HTMLDivElement>(null);

	// Close picker when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const normalizedValue = value?.toLowerCase() || '#000000';
	const overlayColor = getContrastTextColor(normalizedValue);

	return (
		<div className={css(styleSheet.container)}>
			<div
				className={css(styleSheet.colorBox)}
				style={{ backgroundColor: normalizedValue }}
				onClick={() => setIsOpen(open => !open)}
				aria-label='Open color picker'
				tabIndex={0}
				role='button'
			>
				<span className={css(styleSheet.hexOverlay)} style={{ color: overlayColor }}>
					{normalizedValue.toUpperCase()}
				</span>
			</div>
			{isOpen && (
				<div ref={popoverRef} className={css(styleSheet.popover)}>
					<div className={css(styleSheet.popoverContainer)}>
						<HexColorPicker color={value} onChange={onChange} />
						<div>
							<label className={css(styleSheet.inputLabel)} htmlFor='hex-input'>
								Hex
							</label>
							<HexColorInput
								id='hex-input'
								color={value.toUpperCase()}
								onChange={onChange}
								className={css(styleSheet.hexInput)}
								prefixed
								aria-label='Hex color input'
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export { ColorPicker };
